import { useLocation, useNavigate } from 'react-router-dom';
import API from '../../api';
import {
  ClientContainer,
  UserContainer,
  ReportsContainer,
  PagesContainer,
} from '../../providers';
import { sortClientsAscendingByDate } from '../../helpers';
import {
  FORBIDDEN_QUERY_PARAMS_ROUTES,
  QUERY_PARAMS,
} from '../../helpers/query-params-utils';
import { useGetReports } from './reports';
import { PAGINATION_PER_PAGE_LIMITS } from '../../constants';

const { CLIENT } = QUERY_PARAMS;
const { PAST_REPORTS_LIMIT } = PAGINATION_PER_PAGE_LIMITS;

const useGetClients = () => {
  const {
    state: clientState,
    updateClients,
    updateSelectedClient,
    updateClientIsLoading,
    updateClientIsLoaded,
  } = ClientContainer.useContainer();

  const { state: userState } = UserContainer.useContainer();

  const location = useLocation();
  const navigate = useNavigate();

  const getClients = async (businessId = null, updateCurrentClient = false) => {
    const query = {
      businessId: businessId ?? userState?.business?.id,
      fields: 'accounts',
      $limit: 50,
    };
    const response = await API.clients.find(query);
    const clients = response.data.map(client => ({
      ...client,
      accounts: client.accounts.map(account => ({
        service: account.provider.type,
        ...account,
      })),
    }));
    updateClients(clients);
    updateClientIsLoading(false);
    updateClientIsLoaded(true);
    if (updateCurrentClient) {
      updateSelectedClient(clientState.selectedClient.id, clients);
    }
    if (!clientState.selectedClient && response.data?.length) {
      // Update the client in URL query params
      const clientId = sortClientsAscendingByDate(response.data)[0].id;
      if (
        FORBIDDEN_QUERY_PARAMS_ROUTES[CLIENT.name].includes(location.pathname)
      ) {
        // If route doesn't allow query params
        // then, don't update the query params and update the state directly.
        updateSelectedClient(clientId);
      } else {
        /**
         * Disable this since it causes weird double navigations issues.
         * It's also cause unexpected hidden navigations.
         */
        // const fullPath = location.pathname + location.search;
        // navigate(fullPath, { replace: true });
      }
    }
  };

  return getClients;
};

const useCreateClient = () => {
  const { state: clientState, updateClients } = ClientContainer.useContainer();
  const { state: userState } = UserContainer.useContainer();

  const createClient = async data => {
    const businessId = userState?.business?.id;
    const response = await API.clients.create({ businessId, ...data });
    response.accounts = [];
    updateClients([...clientState.clients, response]);
    return response;
  };
  return createClient;
};

const usePatchClient = () => {
  const { state: clientState, updateClients } = ClientContainer.useContainer();
  const { state: userState } = UserContainer.useContainer();
  const businessId = userState.business?.id;
  const updateClient = async (id, data) => {
    const response = await API.clients.update(id, {
      businessId,
      ...data,
    });
    const filteredClients = clientState.clients.filter(
      client => client.id !== id
    );
    updateClients([...filteredClients, response]);
  };
  return updateClient;
};

const useGetClientRecords = () => {
  const { updateReports, resetReportsState } = ReportsContainer.useContainer();
  const { state: userState } = UserContainer.useContainer();

  const getClientRecords = async (clientId, query = { fields: 'provider' }) => {
    resetReportsState();
    const businessId = userState?.business?.id;
    const response = await API.clients.get(clientId, { businessId, ...query });
    const reportsWithPageType = response.reports.map(report => ({
      ...report,
      pageTypes: report.accounts.map(a => a.provider.type),
    }));
    updateReports(reportsWithPageType);
  };
  return getClientRecords;
};

const useDeleteClient = () => {
  const {
    state: clientState,
    updateClients,
    setIsWorkspaceDeleteModal,
    updateSelectedClient,
  } = ClientContainer.useContainer();
  const { state: userState } = UserContainer.useContainer();
  const { state: pagesState, updatePages } = PagesContainer.useContainer();
  const { resetReportsState } = ReportsContainer.useContainer();

  const getReports = useGetReports();

  const businessId = userState.business?.id;
  const deleteClient = async id => {
    await API.clients.delete(id, {
      businessId,
    });
    setIsWorkspaceDeleteModal(false);
    const filteredClients = clientState.clients.filter(
      client => client.id !== id
    );
    const updatedPages = pagesState.pages.map(page => {
      if (page.clientId?.toString() === id.toString())
        return { ...page, clientId: null };
      return page;
    });
    setTimeout(() => {
      updateClients(filteredClients);
    }, 1000);
    updatePages(updatedPages);
    // Set current selected client to null first
    // so that the next valid client can be set using
    // new query params
    updateSelectedClient(null);

    const clientId = filteredClients[0]?.id;
    if (clientId) {
      resetReportsState();
      await getReports({
        clientId,
        fields: 'account,schedule',
        $limit: PAST_REPORTS_LIMIT,
        $skip: 0,
        '$sort[createdAt]': '-1',
      });
    }
  };
  return deleteClient;
};

export {
  useGetClients,
  useCreateClient,
  useGetClientRecords,
  usePatchClient,
  useDeleteClient,
};
